import { useEffect } from 'preact/hooks';
import type { TargetProps } from '../types';

export const POPOVER_CONTENT_SELECTOR = '[data-popovercontent]';

/**
 * This hook triggers a provided handler when a mouseup is detected outside the popover, enabling
 * controlled dismissal (e.g., closing the popover or resetting states). The actual close logic
 * is managed within the handler, keeping responsibility external to the hook itself.
 */
export function useOutsideClickHandler(
  targetRef: TargetProps['ref'],
  isOpen: boolean,
  handler: () => void
) {
  function onMouseUp(e: MouseEvent) {
    const target = e.target as HTMLElement;

    if (target.nodeName === 'DIALOG') {
      handler();
      return;
    }

    if (
      !target.closest(POPOVER_CONTENT_SELECTOR) &&
      !targetRef.current?.contains(target)
    ) {
      handler();
    }
  }

  useEffect(() => {
    if (isOpen) {
      // Note: Uses "mouseup" instead of "click" to ensure the current popover closes
      // before a new popover target is triggered (avoids overlapping interactions).
      document.addEventListener('mouseup', onMouseUp);
    }
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [isOpen]);
}
